import React, { useEffect, useState } from 'react';
import { createTheme, ThemeProvider, Button, SvgIcon } from '@mui/material';
import PropTypes from 'prop-types';
import { object, string } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Container, Form, Col, Row } from 'react-bootstrap';
import { SelectInput, TextInput, StyledRadioInput, AutocompleteInput } from "../../../Helpers/Inputs";
import { NumberFormatDollars } from '../../../Helpers/Formatters';
import SyringeIcon from '../../../../icons/SyringeBlue.svg?react';
import LaborIcon from '../../../../icons/labor.svg?react';
import LightLaborIcon from '../../../../icons/lightLabor.svg?react';
import ProfessionalIcon from '../../../../icons/professional.svg?react';
import TechnicalIcon from '../../../../icons/technical.svg?react';
import ProtectMyFutureIcon from '../../../../icons/protectMyFuture.svg?react';
import MoneyIcon from '../../../../icons/money.svg?react';
import MaternityLeaveIcon from '../../../../icons/maternityLeave.svg?react';
import './EmploymentDetails.scss';

const OCCUPATION_NOT_FOUND = "CAN'T FIND YOUR OCCUPATION?";

export default function EmploymentDetails(props) {
    const [occupationOptions, setOccupationOptions] = useState([]);
    const [isDisabled, setIsDisabled] = useState(false);
    const [criteria, setCriteria] = useState([])
    const [occupationList, setOccupationList] = useState([]);
    const [isFetching, setIsFetching] = useState(false);

    const formMethods = useForm({
        resolver: yupResolver(EmploymentDetails.ValidationSchema(criteria && criteria.length)),
        mode: 'all',
        reValidateMode: 'all',
        defaultValues: props.formState
    });

    const { handleSubmit, trigger } = formMethods;

    // manually call setValue for fields that don't play nice with react-hook-form register
    const handleValueChange = (field, value) => {
        props.onFieldChange(field, value);
    };

    const handleCurrentOccupationValueChange = (field, value) => {
        handleValueChange(field, value);
        trigger(field);
    };

    const handleOccupationCategoryValueChange = (field, value) => {
        handleValueChange(field, value);
        trigger(field);
    };

    const handleOccupationValueChange = (field, value) => {
        handleValueChange(field, value);
        trigger(field);
    };

    const handleWorkHoursValueChange = (field, value) => {
        handleValueChange(field, value);
        trigger(field);
    };

    const handleDescriptionValueChange = (field, value) => {
        handleValueChange(field, value);
        trigger(field);
    };

    useEffect(() => {
        if (props.formState.currentOccupation !== '' && props.formState.currentOccupation !== OCCUPATION_NOT_FOUND) {
            const criteriaListOptions = props.formState.occupationList.find(e => e.value === props.formState.currentOccupation);
            const criteriaList = criteriaListOptions && criteriaListOptions.criteria;
            setCriteria(criteriaList);
        }
    }, [props.formState.currentOccupation]);

    let debounce = null;

    const handleInputChange = (e, val) => {
        if (val.length !== 3) {
            setOccupationOptions([]); // Clear options if less than 3 characters
            setIsFetching(false); // Stop fetching state
            return;
        }

        let isSubscribed = true;
        if (debounce) {
            clearTimeout(debounce);
        }

        debounce = setTimeout(() => getOccupation(() => isSubscribed), 1000);
        setIsFetching(true); // Indicate API call in progress
        setIsDisabled(true);
        const getOccupation = () => {

            const queryParams = new URLSearchParams({
                term: val,
                limit: 25,
                state: props.basicInfo.state
            });
            const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL
            fetch(`${covrServicesUrl}/api/Case/GetOccupationList?${queryParams.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            })
                .then(res => res.json())
                .then((data) => {
                    if (data.isSuccess && data.result && data.result.length > 0) {
                        setOccupationList(data.result);
                        const opt = [];
                        const fullForm = { ...props.fullFormState };
                        data.result.map((index) => {
                            const criteria = []

                            if (index.subCriteria && index.subCriteria.length > 0) {
                                index.subCriteria.map(ind => {
                                    criteria.push({ label: ind.text, value: ind.text })
                                })
                            }
                            opt.push({ label: index.jobTitle, value: index.jobTitle, criteria: criteria })
                        })
                        fullForm.pages.employmentDetails.occupationList = opt;
                        props.onMultiFieldChange(fullForm);
                        setOccupationOptions(opt);
                    }
                    setIsFetching(false);
                    setIsDisabled(false);
                })
                .catch(() => {
                    setIsFetching(false);
                    setIsDisabled(false);
                });
        };

        return () => {
            clearTimeout(debounce);
            isSubscribed = false;
        };
    };

    const getDescribeOptions = [
        { label: "Protect my future", value: "Protect my future", secondaryText: "Looking for disability insurance to protect me if I become disabled in the future and can't work.", icon: ProtectMyFutureIcon },
        { label: "Maternity Leave", value: "Maternity Leave", secondaryText: "Looking for coverage to supplement my income during maternity leave.", icon: MaternityLeaveIcon },
        { label: "Financial Assistance", value: "Financial Assistance", secondaryText: "Currently disabled or not working and I'm looking for financial assistance.", icon: MoneyIcon }
    ]

    const getOccupationOptions = [
        { label: "Professional", value: "Professional", secondaryText: "Office job with few hazards. Examples: accountant, pharmacist, software engineer.", icon: ProfessionalIcon },
        { label: "Technical", value: "Technical", secondaryText: "Similar to Professional, but with some hazards. Examples: salesperson, dental hygienist, lab tech.", icon: TechnicalIcon },
        { label: "Light Labor", value: "Light Labor", secondaryText: "Skilled/manual jobs in lighter industries. Examples: auto mechanic, carpenter, landscaper.", icon: LightLaborIcon },
        { label: "Labor", value: "Labor", secondaryText: "Heavier or unskilled manual jobs with more hazards. Examples: ride share driver, roofer, etc.", icon: LaborIcon },
    ];

    const lastListItem = (theme) => {
        return {
            components: {
                MuiInputBase: {
                    styleOverrides: {
                        input: {
                            fontSize: 16,
                        },
                        root: {
                            height: 56,
                        },
                    },
                },
                MuiFilledInput: {
                    styleOverrides: {
                        root: {
                            backgroundColor: "rgba(0, 0, 0, 0.05)",
                        },
                        underline: {
                            "&::before": {
                                borderBottom: "solid 2px #b2b2b2",
                            },
                            "&:hover::before": {
                                borderBottomWidth: "2px",
                            },
                        },
                    },
                },
                MuiFormLabel: {
                    styleOverrides: {
                        root: {
                            fontSize: 16,
                            color: "rgba(0,0,0,0.6)",
                        }
                    },
                },
                MuiFormHelperText: {
                    styleOverrides: {
                        root: {
                            marginTop: 0,
                            paddingTop: 0,
                        },
                    },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        shrink: {
                            color: "rgba(0,0,0,0.3) !important",
                        },
                    },
                },
                MuiNativeSelect: {
                    styleOverrides: {
                        fontSize: 16,
                        icon: {
                            color: "rgba(0,0,0,1)"
                        },
                        iconFilled: {
                            right: 16,
                        },
                    },
                },
                MuiAutocomplete: {
                    styleOverrides: {
                        endAdornment: {
                            display: "none"
                        },
                        option: {
                            "&:last-child": {
                                color: theme.palette.primary.main,
                                fontWeight: "bold",
                            }
                        },
                    },
                },
            },
        };
    }
    const onSubmit = () => {
        const fullForm = { ...props.fullFormState };
        if (occupationList) {
            const selectedOccupation = occupationList && occupationList.find(el => el.jobTitle === props.formState.currentOccupation);
            if (selectedOccupation && selectedOccupation.occClasses.length > 0) {
                fullForm.pages.employmentDetails.occupationClass = selectedOccupation.occClasses[0].occClass;
                fullForm.pages.employmentDetails.discounts = selectedOccupation.occClasses[0].discounts;
            } else if (selectedOccupation && occupationOptions && props.formState.currentOccupation !== '' && props.formState.currentOccupation !== OCCUPATION_NOT_FOUND && criteria && criteria.length > 0) {
                const selectedCriteria = selectedOccupation.subCriteria.find(el => el.text === props.formState.occupationCategory);
                fullForm.pages.employmentDetails.occupationClass = selectedCriteria.occClasses[0].occClass;
                fullForm.pages.employmentDetails.discounts = selectedCriteria.occClasses[0].discounts;
            }
            props.onMultiFieldChange(fullForm);
            props.onNext();
        }
    }

    const filterOccupationOptions = (options, state) => {
        const newOptions = [];
        if (state.inputValue.length >= 3) {
            options.forEach((element) => {
                if (element.label.toLowerCase().includes(state.inputValue.toLowerCase())) newOptions.push(element);
            });
            if (newOptions.length > 0 || (!isDisabled && newOptions.length === 0 && state.inputValue.length >= 3))
                newOptions.push({ label: OCCUPATION_NOT_FOUND, value: OCCUPATION_NOT_FOUND, criteria: [] })
        }
        return newOptions;
    }

    return (
        <ThemeProvider theme={(theme) => createTheme({
            ...theme,
            ...lastListItem(theme),
        })}>
            <Container className="employment-details">
                <Row className="justify-content-center pt-2">
                    <div className="icon-container background-color-16">
                        <SvgIcon component={SyringeIcon} fontSize="large" color="primary" />
                    </div>
                </Row>
                <Row className="justify-content-center mainTitle">
                    <h4>Employement Details</h4>
                </Row>
                <FormProvider {...formMethods}>
                    <Form onSubmit={handleSubmit(onSubmit)} >
                        <Row className="justify-content-center">
                            <Col lg={4} className='w-100'>
                                <div className='form-group mb-3'>
                                    <AutocompleteInput
                                        name="currentOccupation"
                                        id="currentOccupation"
                                        defaultValue={props.formState.currentOccupation}
                                        onInputChange={(e, val, reason) => { if (reason === 'input') handleInputChange(e, val) }}
                                        label="Current Occupation"
                                        isFetching={isFetching}
                                        selectOptions={occupationOptions}
                                        onChange={(value) => handleCurrentOccupationValueChange('currentOccupation', value)}
                                        filterOptions={(options, state) => filterOccupationOptions(options, state)}
                                    />
                                </div>
                                {occupationOptions && props.formState.currentOccupation !== '' && props.formState.currentOccupation !== OCCUPATION_NOT_FOUND && criteria && criteria.length > 0 &&
                                    <div className='form-group mb-3'>
                                        <SelectInput
                                            name="occupationCategory"
                                            id="occupationCategory"
                                            value={props.formState.occupationCategory}
                                            onChange={e => handleOccupationCategoryValueChange('occupationCategory', e.target.value)}
                                            label="Occupational Category"
                                            selectOptions={criteria}
                                            placeholder="Select a Category"
                                        />
                                    </div>
                                }
                                {props.formState.currentOccupation === OCCUPATION_NOT_FOUND &&
                                    <div className='form-group mb-3'>
                                        <SelectInput
                                            name="occupation"
                                            id="occupation"
                                            value={props.formState.occupation}
                                            onChange={e => handleOccupationValueChange('occupation', e.target.value)}
                                            label="Occupation Type"
                                            selectOptions={getOccupationOptions}
                                            isImageOptions={true}
                                            renderValue={(value) => {
                                                return (value)
                                            }}
                                        />
                                    </div>
                                }
                                {props.formState.currentOccupation === OCCUPATION_NOT_FOUND &&
                                    <TextInput
                                        name="jobTitle"
                                        id="jobTitle"
                                        value={props.formState.jobTitle}
                                        onChange={(e) => handleValueChange('jobTitle', e.target.value)}
                                        label="Job Title"
                                    />
                                }
                                <div className='mt-3 form-group mb-0'>
                                    <StyledRadioInput
                                        name="workHours"
                                        id="workHours"
                                        label="Do you work at least 30 hours a week?"
                                        value={props.formState.workHours}
                                        options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                        onChange={e => handleWorkHoursValueChange('workHours', e)}
                                    />
                                </div>
                                <div className='form-group mb-3'>
                                    <TextInput
                                        name="annualIncome"
                                        id="annualIncome"
                                        label="Annual Income"
                                        value={props.formState.annualIncome}
                                        onChange={e => handleValueChange('annualIncome', e.target.value)}
                                        InputProps={{
                                            inputComponent: NumberFormatDollars,
                                        }}
                                    />
                                </div>
                                <div className="form-group mb-0">
                                    <SelectInput
                                        name="description"
                                        id="description"
                                        value={props.formState.description}
                                        onChange={e => handleDescriptionValueChange('description', e.target.value)}
                                        label="Reason for Coverage"
                                        selectOptions={getDescribeOptions}
                                        isImageOptions={true}
                                        renderValue={(value) => {
                                            return (value)
                                        }}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row className="nav-control">
                            <Col className="text-start btn-back">
                                <Button variant="outlined" size="medium" color="primary" className="px-4" onClick={props.onBack} >BACK</Button>
                            </Col>
                            <Col className="text-end">
                                <Button variant="contained" size="medium" color="primary" type="submit" className="px-4">NEXT</Button>
                            </Col>
                        </Row>
                    </Form>
                </FormProvider>
            </Container>
        </ThemeProvider>
    );
}

EmploymentDetails.propTypes = {
    formState: PropTypes.object.isRequired,
    onNext: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    hideBack: PropTypes.bool,
};

EmploymentDetails.ValidationSchema = (criteria) => {
    return object().shape({
        currentOccupation: string().required('This field is required'),
        occupationCategory: string().when('currentOccupation', {
            is: (currentOccupation) => currentOccupation !== OCCUPATION_NOT_FOUND && criteria > 0,
            then: (schema) => schema.required('This field is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        occupation: string().when('currentOccupation', {
            is: (currentOccupation) => currentOccupation === OCCUPATION_NOT_FOUND,
            then: (schema) => schema.required('This field is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        jobTitle: string().when(['occupation', 'currentOccupation'], {
            is: (occupation, currentOccupation) => occupation === OCCUPATION_NOT_FOUND && currentOccupation === OCCUPATION_NOT_FOUND,
            then: (schema) => schema.required('This field is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        workHours: string().required('This field is required'),
        annualIncome: string().required('This field is required'),
        description: string().required('This field is required')
    })
};

EmploymentDetails.DefaultFormState = (prefill = false) => {
    return {
        occupation: '',
        workHours: '',
        annualIncome: '',
        description: '',
        currentOccupation: '',
        occupationCategory: '',
        jobTitle: '',
        occupationClass: '',
        discounts: [],
    };
};
